import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import ChatWindow from '../../components/chat/ChatWindow';
import { FaComments, FaArrowLeft } from 'react-icons/fa';

const PageContainer = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled.h1`
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 2rem;
`;

const PartnersContainer = styled.div`
  margin-bottom: 2rem;
`;

const PartnerItem = styled.div`
  display: flex;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  
  &:last-child {
    border-bottom: none;
  }
`;

const PartnerAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.border};
  margin-right: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.primary};
`;

const PartnerInfo = styled.div`
  flex-grow: 1;
`;

const PartnerName = styled.div`
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const PartnerDetails = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9rem;
`;

const ChatButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ChatWithPartner = () => {
  const { currentUser } = useAuth();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPartner, setSelectedPartner] = useState(null);
  
  useEffect(() => {
    const fetchPartners = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        
        // Get all partner references for this user
        const partnersCollectionRef = collection(db, 'users', currentUser.uid, 'partners');
        const partnersSnapshot = await getDocs(partnersCollectionRef);
        
        // Fetch complete partner data
        const partnersData = await Promise.all(
          partnersSnapshot.docs.map(async (partnerDoc) => {
            const partnerId = partnerDoc.id;
            const partnerRef = doc(db, 'partners', partnerId);
            const partnerSnapshot = await getDoc(partnerRef);
            
            if (partnerSnapshot.exists()) {
              return {
                id: partnerId,
                ...partnerSnapshot.data(),
                assignedAt: partnerDoc.data().assignedAt
              };
            }
            
            return null;
          })
        );
        
        // Filter out any null values (partners that might have been deleted)
        const validPartners = partnersData.filter(partner => partner !== null);
        
        setPartners(validPartners);
      } catch (err) {
        console.error('Error fetching partners:', err);
        setError('Failed to load your nutrition partners');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPartners();
  }, [currentUser]);

  const handleSelectPartner = (partnerId) => {
    setSelectedPartner(partnerId);
  };
  
  const handleBackToPartners = () => {
    setSelectedPartner(null);
  };

  // If a partner is selected, show the chat window
  if (selectedPartner) {
    return (
      <PageContainer>
        <BackButton onClick={handleBackToPartners} variant="outline">
          <FaArrowLeft /> Back to Partners
        </BackButton>
        <ChatWindow partnerId={selectedPartner} clientId={currentUser.uid} isPartnerView={false} />
      </PageContainer>
    );
  }
  
  return (
    <PageContainer>
      <Title>Chat with Partners</Title>
      <Subtitle>Connect with your assigned nutrition partners</Subtitle>
      
      {loading && (
        <LoadingState>
          <h3>Loading partners...</h3>
          <p>Please wait while we fetch your partner data.</p>
        </LoadingState>
      )}
      
      {error && <p style={{ color: 'red' }}>{error}</p>}
      
      {!loading && !error && partners.length === 0 && (
        <EmptyState>
          <h3>No Partners Found</h3>
          <p>You don't have any assigned nutrition partners yet.</p>
        </EmptyState>
      )}
      
      {!loading && !error && partners.length > 0 && (
        <Card>
          <PartnersContainer>
            {partners.map(partner => (
              <PartnerItem key={partner.id}>
                <PartnerAvatar>
                  {partner.displayName?.charAt(0).toUpperCase() || 'P'}
                </PartnerAvatar>
                <PartnerInfo>
                  <PartnerName>{partner.displayName || 'Partner'}</PartnerName>
                  <PartnerDetails>
                    {partner.specialization || 'Nutrition Specialist'} • Since: {partner.assignedAt ? new Date(partner.assignedAt.toDate()).toLocaleDateString() : 'N/A'}
                  </PartnerDetails>
                </PartnerInfo>
                <ChatButton 
                  variant="primary"
                  onClick={() => handleSelectPartner(partner.id)}
                >
                  <FaComments /> Chat Now
                </ChatButton>
              </PartnerItem>
            ))}
          </PartnersContainer>
        </Card>
      )}
    </PageContainer>
  );
};

export default ChatWithPartner; 